import Floatbar from "@app/components/Floatbar";
import { useSetToast } from "@app/components/Toast";
import {
    getSelectedIds,
    getSelectedRowsCount,
} from "@app/shared/utils/paginatedSelection";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { type PaginatedSelectedRows } from "@design-system/Table";
import { QuestionnaireStatus, ResponseStatus } from "@generated/client/graphql";
import { useState, type FC } from "react";
import {
    useCloseQuestionnaires,
    useDeleteSuppliers,
    useOpenQuestionnaires,
    useReOpenQuestionnaires,
    useSendQuestionnaireEmails,
} from "../../data";
import { type SuppliersMinimalListItem } from "../../types";
import { DeleteSupplierModal } from "../DeleteSupplierModal";
import { OpenQuestionnaireModal } from "../OpenQuestionnaireModal";
import { SendQuestionnaireEmailModal } from "../SendQuestionnaireEmailModal";
import { useSupplierTableTranslations } from "./i18n";

const actions = {
    SEND_QUESTIONNAIRE: "SEND_QUESTIONNAIRE",
    SEND_REMINDER: "SEND_REMINDER",
    CLOSE_QUESTIONNAIRE: "CLOSE_QUESTIONNAIRE",
    REOPEN_QUESTIONNAIRE: "REOPEN_QUESTIONNAIRE",
    DELETE_SUPPLIER: "DELETE_SUPPLIER",
} as const;

export const ActionBar: FC<{
    unset: () => void;
    paginatedSelectedRows: PaginatedSelectedRows;
    rowCount: number;
    suppliers: SuppliersMinimalListItem[];
    isSuppliersPending: boolean;
}> = ({
    unset,
    paginatedSelectedRows,
    rowCount,
    suppliers,
    isSuppliersPending,
}) => {
    const { closeQuestionnaires, isPending: isCloseQuestionnairePending } =
        useCloseQuestionnaires();
    const { openQuestionnaires } = useOpenQuestionnaires();
    const { reOpenQuestionnaires, isPending: isReOpenQuestionnairePending } =
        useReOpenQuestionnaires();
    const { sendQuestionnaireEmails } = useSendQuestionnaireEmails();
    const { deleteSuppliers } = useDeleteSuppliers();
    const { setToastSuccess, setToastError } = useSetToast();

    const [activeAction, setActiveAction] = useState<string | undefined>(
        undefined,
    );

    const selectedRowsCount = getSelectedRowsCount(
        paginatedSelectedRows,
        rowCount,
    );

    const selectedSupplierIds = getSelectedIds(
        paginatedSelectedRows,
        suppliers.map((supplier) => supplier.id),
    );

    const selectedSuppliers = suppliers.filter((supplier) =>
        selectedSupplierIds.includes(supplier.id),
    );

    const selectedSuppliersWithOpenQuestionnaire = selectedSuppliers.filter(
        (supplier) =>
            supplier.lastQuestionnaire?.status === QuestionnaireStatus.Open,
    );

    const selectedSuppliersWithClosedQuestionnaire = selectedSuppliers.filter(
        (supplier) =>
            supplier.lastQuestionnaire?.status === QuestionnaireStatus.Closed,
    );

    const selectedSuppliersWithoutOpenQuestionnaire = selectedSuppliers.filter(
        (supplier) =>
            supplier.lastQuestionnaire?.status !== QuestionnaireStatus.Open,
    );

    const selectedSuppliersWithQuestionnaire = selectedSuppliers.filter(
        (supplier) => !!supplier.lastQuestionnaire,
    );

    const isFloatbarOpen = !!selectedRowsCount;

    const isSendQuestionnaireDisabled =
        !!selectedSuppliersWithOpenQuestionnaire.length || isSuppliersPending;

    const isSendEmailDisabled =
        !!selectedSuppliersWithoutOpenQuestionnaire.length ||
        isSuppliersPending;

    const isDeleteSupplierDisabled =
        !!selectedSuppliersWithQuestionnaire.length || isSuppliersPending;

    const isCloseQuestionnaireDisabled =
        selectedSuppliersWithOpenQuestionnaire.length === 0;

    const isReOpenQuestionnaireDisabled =
        selectedSuppliersWithClosedQuestionnaire.length === 0;

    const handleActionChange = (action: string) => {
        return () => {
            setActiveAction(action);
        };
    };

    const handleModalOpenChange = () => {
        setActiveAction(undefined);
    };

    const handleOpenModalConfirm = (
        questionnaireTagId: string,
        name?: string,
        deadline?: Date,
    ) => {
        openQuestionnaires(
            {
                supplierIds: selectedSupplierIds,
                questionnaireTagId,
                questionnaireName: name,
                deadline,
            },
            {
                onSettled: (data, error) => {
                    if (
                        error ||
                        data?.openQuestionnaires?.status !==
                            ResponseStatus.Success
                    ) {
                        setToastError(
                            t(keys.action_bar_reopen_questionnaire_error),
                        );
                        return;
                    }
                    setToastSuccess(
                        t(keys.action_bar_reopen_questionnaire_success),
                    );
                },
            },
        );

        setActiveAction(undefined);
        unset();
    };

    const handleSendEmailModalConfirmClick = (
        senderName: string,
        emailSubject: string,
        emailText: string,
        emailHtml: string,
        isTest: boolean,
    ) => {
        sendQuestionnaireEmails(
            {
                questionnaireIds: selectedSuppliers
                    .map((supplier) => supplier.lastQuestionnaire?.id)
                    .filter((qId) => qId != null),
                email: {
                    senderName: senderName,
                    subject: emailSubject,
                    text: emailText,
                    html: emailHtml,
                },
                isTest,
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.sendSupplierQuestionnaireEmails?.status !==
                            ResponseStatus.Success
                    ) {
                        setToastError(t(keys.send_email_error));
                        return;
                    }
                    setToastSuccess(t(keys.send_email_success));
                },
            },
        );
        if (isTest) {
            return;
        }

        setActiveAction(undefined);
        unset();
    };

    const handleQuestionnaireClose = () => {
        const questionnaireIds = selectedSuppliersWithOpenQuestionnaire
            .map((supplier) => supplier.lastQuestionnaire?.id)
            .filter((qId) => qId != null);

        closeQuestionnaires(questionnaireIds, {
            onSettled(data, error) {
                if (
                    error ||
                    data?.closeQuestionnaires?.status !== ResponseStatus.Success
                ) {
                    setToastError(t(keys.action_bar_close_questionnaire_error));
                    return;
                }
                setToastSuccess(t(keys.action_bar_close_questionnaire_success));
            },
        });

        setActiveAction(undefined);
        unset();
    };

    const handleQuestionnaireReOpen = () => {
        const questionnaireIds = selectedSuppliers
            .filter(
                (supplier) =>
                    supplier.lastQuestionnaire?.status ===
                    QuestionnaireStatus.Closed,
            )
            .map((supplier) => supplier.lastQuestionnaire?.id)
            .filter((qId) => qId != null);

        reOpenQuestionnaires(questionnaireIds, {
            onSettled(data, error) {
                if (
                    error ||
                    data?.reOpenQuestionnaires?.status !==
                        ResponseStatus.Success
                ) {
                    setToastError(
                        t(keys.action_bar_reopen_questionnaire_error),
                    );
                    return;
                }
                setToastSuccess(
                    t(keys.action_bar_reopen_questionnaire_success),
                );
            },
        });

        setActiveAction(undefined);
        unset();
    };

    const handleDeleteSupplier = () => {
        if (selectedSuppliersWithOpenQuestionnaire.length) {
            return;
        }
        deleteSuppliers(selectedSupplierIds, {
            onSettled: (data, error) => {
                if (
                    error ||
                    data?.deleteSuppliers?.status !== ResponseStatus.Success
                ) {
                    setToastError(t(keys.action_bar_delete_supplier_error));
                    return;
                }
                setToastSuccess(t(keys.action_bar_delete_supplier_success));
            },
        });

        setActiveAction(undefined);
        unset();
    };

    const { t, keys } = useSupplierTableTranslations();

    return (
        <>
            <Floatbar
                unset={unset}
                text={t(keys.action_bar_selected_suppliers, {
                    count: selectedRowsCount,
                })}
                open={isFloatbarOpen}
                primary={
                    <Dropdown>
                        <Dropdown.Trigger asChild>
                            <Button variant="outlined" size="sm">
                                {t(keys.action_bar_select_action)}
                                <Button.Icon name="chevronDown" size="sm" />
                            </Button>
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <Dropdown.Item
                                onClick={handleActionChange(
                                    actions.SEND_QUESTIONNAIRE,
                                )}
                            >
                                <Dropdown.ItemIcon name="plus" />
                                <Dropdown.ItemText
                                    variant="body-sm"
                                    className="text-primary"
                                >
                                    {t(keys.action_bar_send_questionnaire)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={handleActionChange(
                                    actions.SEND_REMINDER,
                                )}
                            >
                                <Dropdown.ItemIcon name="calendar" />
                                <Dropdown.ItemText
                                    variant="body-sm"
                                    className="text-primary"
                                >
                                    {t(keys.action_bar_send_reminder)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                            {!isCloseQuestionnaireDisabled && (
                                <Dropdown.Item
                                    onClick={handleActionChange(
                                        actions.CLOSE_QUESTIONNAIRE,
                                    )}
                                >
                                    <Dropdown.ItemIcon name="close" />
                                    <Dropdown.ItemText
                                        variant="body-sm"
                                        className="text-primary"
                                    >
                                        {t(keys.action_bar_close_questionnaire)}
                                    </Dropdown.ItemText>
                                </Dropdown.Item>
                            )}
                            {!isReOpenQuestionnaireDisabled && (
                                <Dropdown.Item
                                    onClick={handleActionChange(
                                        actions.REOPEN_QUESTIONNAIRE,
                                    )}
                                >
                                    <Dropdown.ItemIcon name="arrowUndo" />
                                    <Dropdown.ItemText
                                        variant="body-sm"
                                        className="text-primary"
                                    >
                                        {t(
                                            keys.action_bar_reopen_questionnaire,
                                        )}
                                    </Dropdown.ItemText>
                                </Dropdown.Item>
                            )}
                            <Dropdown.Item
                                onClick={handleActionChange(
                                    actions.DELETE_SUPPLIER,
                                )}
                                variant="negative"
                            >
                                <Dropdown.ItemIcon name="trash" />
                                <Dropdown.ItemText
                                    variant="body-sm"
                                    className="text-primary"
                                >
                                    {t(keys.action_bar_delete_supplier)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                        </Dropdown.Content>
                    </Dropdown>
                }
                actions={[
                    {
                        id: actions.CLOSE_QUESTIONNAIRE,
                        apply: (
                            <Button
                                size="sm"
                                onClick={handleQuestionnaireClose}
                                disabled={isSuppliersPending}
                                loading={isCloseQuestionnairePending}
                            >
                                {t(keys.action_bar_close)}
                            </Button>
                        ),
                        text: t(keys.action_bar_close_questionnaire, {
                            count: selectedSuppliersWithOpenQuestionnaire.length,
                        }),
                    },
                    {
                        id: actions.REOPEN_QUESTIONNAIRE,
                        apply: (
                            <Button
                                size="sm"
                                onClick={handleQuestionnaireReOpen}
                                disabled={isSuppliersPending}
                                loading={isReOpenQuestionnairePending}
                            >
                                {t(keys.action_bar_reopen)}
                            </Button>
                        ),
                        text: t(keys.action_bar_reopen_questionnaire, {
                            count: selectedSuppliersWithClosedQuestionnaire.length,
                        }),
                    },
                ]}
                activeAction={activeAction}
                setActiveAction={setActiveAction}
            />
            <OpenQuestionnaireModal
                open={activeAction === actions.SEND_QUESTIONNAIRE}
                onOpenChange={handleModalOpenChange}
                onConfirm={handleOpenModalConfirm}
                disabled={isSendQuestionnaireDisabled}
                disabledTooltip={t(
                    keys.action_bar_send_questionnaire_disabled,
                    { count: selectedSuppliersWithOpenQuestionnaire.length },
                )}
            />
            <SendQuestionnaireEmailModal
                open={activeAction === actions.SEND_REMINDER}
                onOpenChange={handleModalOpenChange}
                onSendClick={handleSendEmailModalConfirmClick}
                disabled={isSendEmailDisabled}
                disabledTooltip={t(keys.action_bar_send_email_disabled, {
                    count: selectedSuppliersWithoutOpenQuestionnaire.length,
                })}
            />
            <DeleteSupplierModal
                open={activeAction === actions.DELETE_SUPPLIER}
                onOpenChange={handleModalOpenChange}
                onDelete={handleDeleteSupplier}
                supplierCount={selectedRowsCount}
                disabled={isDeleteSupplierDisabled}
                disabledTooltip={t(keys.action_bar_delete_supplier_disabled, {
                    count: selectedSuppliersWithQuestionnaire.length,
                })}
            />
        </>
    );
};
